import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import {
	array as yupArray,
	bool as yupBool,
	date as yupDate,
	number as yupNumber,
	object as yupObject,
	ref as yupRef,
	string as yupString,
} from 'yup'
import { useCountryShippingDefault } from '../../useCountryShippingDefault'
import { useFormFieldsLabels } from './useFormFieldsLabels'
import { useFormFieldsNames } from './useFormFieldsNames'

export const useFormFieldsValidations = () => {
	const {
		billingLastName,
		careOf,
		city,
		collections,
		company,
		country,
		countryCode,
		dob,
		email,
		emailConfirm,
		emailForgotten,
		fileUpload,
		firstName,
		gender,
		giftcardCode,
		iban,
		jobPosition,
		lastName,
		message,
		orderEmail,
		orderNumber,
		password,
		passwordConfirm,
		passwordCurrent,
		passwordNew,
		postCode,
		privacyPolicy,
		quoteAdditionalDescription,
		quoteCustomerId,
		quoteDescription,
		quoteName,
		quoteNameType,
		reason,
		region,
		regionId,
		regionText,
		street,
		telephone,
		username,
	} = useFormFieldsNames()

	const {
		billingLastName: billingLastNameLabel,
		careOf: careOfLabel,
		city: cityLabel,
		collections: collectionsLabel,
		country: countryLabel,
		countryCode: countryCodeLabel,
		dob: dobLabel,
		email: emailLabel,
		emailConfirm: emailConfirmLabel,
		fileUpload: fileUploadLabel,
		firstName: firstNameLabel,
		gender: genderLabel,
		giftcardCode: giftcardCodeLabel,
		iban: ibanLabel,
		jobPosition: jobPositionLabel,
		lastName: lastNameLabel,
		message: messageLabel,
		orderNumber: orderNumberLabel,
		password: passwordLabel,
		passwordConfirm: passwordConfirmLabel,
		passwordNew: passwordNewLabel,
		privacyPolicy: privacyPolicyLabel,
		quoteAdditionalDescription: quoteAdditionalDescriptionLabel,
		quoteCustomerId: quoteCustomerIdLabel,
		quoteDescription: quoteDescriptionLabel,
		quoteName: quoteNameLabel,
		quoteNameType: quoteNameTypeLabel,
		reason: reasonLabel,
		region: regionLabel,
		street: streetLabel,
		telephone: telephoneLabel,
		username: usernameLabel,
	} = useFormFieldsLabels()

	const { formatMessage } = useIntl()
	const { country: defaultShippingCountry } = useCountryShippingDefault()

	return useMemo(
		() => ({
			[billingLastName]: yupString().required().isAlpha().label(billingLastNameLabel),
			[careOf]: yupString().label(careOfLabel),
			[collections]: yupObject()
				.test({
					test: (obj) => Object.values(obj).some(Boolean),
				})
				.label(collectionsLabel),
			[company]: yupString(),
			[city]: yupString().required().label(cityLabel),
			[country]: yupString().required().label(countryLabel),
			[countryCode]: yupString().required().label(countryCodeLabel),
			[dob]: yupDate()
				.nullable()
				.transform((curr, orig) => (orig === '' ? null : curr))
				.max(new Date(), formatMessage({ id: 'yup_dob_max' }))
				.typeError(formatMessage({ id: 'yup_dob_invalid' }))
				.required()
				.label(dobLabel),
			[username]: yupString().required().label(usernameLabel),
			[email]: yupString().email().required().label(emailLabel),
			[emailConfirm]: yupString().required().isEqualTo(yupRef(email)).label(emailConfirmLabel),
			[emailForgotten]: yupString().email().required().label(emailLabel),
			[fileUpload]: yupArray().fileUpload().label(fileUploadLabel),
			[firstName]: yupString().required().isAlpha().label(firstNameLabel),
			[gender]: yupString().required().label(genderLabel),
			[iban]: yupString().required().label(ibanLabel),
			[jobPosition]: yupString().required().label(jobPositionLabel),
			[lastName]: yupString().required().isAlpha().label(lastNameLabel),
			[message]: yupString().max(500).required().label(messageLabel),
			[orderEmail]: yupString().email().required().label(emailLabel),
			[orderNumber]: yupString().required().label(orderNumberLabel),
			[password]: yupString().required().label(passwordLabel),
			[passwordConfirm]: yupString().required().isEqualTo(yupRef(password)).label(passwordConfirmLabel),
			[passwordCurrent]: yupString().required().label(passwordLabel),
			[passwordNew]: yupString().required().isDifferentFrom(yupRef(passwordCurrent)).password().label(passwordNewLabel),
			[postCode]: yupString(),
			[privacyPolicy]: yupBool().oneOf([true], privacyPolicyLabel),
			[reason]: yupString().required().label(reasonLabel),
			[region]: yupString().required().label(regionLabel),
			[regionId]: yupNumber().label(regionLabel),
			[regionText]: yupString().required().label(regionLabel),
			[street]: yupArray()
				.transform((values) => values.map(({ value }) => value?.trim()))
				.isAtLeastOneNotEmpty()
				.label(streetLabel),
			[telephone]: yupString()
				.required()
				.telephone({ defaultCountry: defaultShippingCountry?.code })
				.label(telephoneLabel),
			[giftcardCode]: yupString().required().label(giftcardCodeLabel),
			[quoteNameType]: yupString().required().label(quoteNameTypeLabel),
			[quoteName]: yupString().required().label(quoteNameLabel),
			[quoteCustomerId]: yupString().required().label(quoteCustomerIdLabel),
			[quoteDescription]: yupString().required().label(quoteDescriptionLabel),
			[quoteAdditionalDescription]: yupString().required().label(quoteAdditionalDescriptionLabel),
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)
}
